import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Users, UserPlus, Target } from "lucide-react";
import { ROLES } from "@/lib/roles";
import { toast } from "sonner";

const CAND_STAGES = [
  { value: "applied",    label: "Applied",    color: "#0EA5FF" },
  { value: "screening",  label: "Screening",  color: "#F59E0B" },
  { value: "interview",  label: "Interview",  color: "#8B5CF6" },
  { value: "offer",      label: "Offer",      color: "#5FBF3A" },
  { value: "hired",      label: "Hired",      color: "#15803D" },
  { value: "rejected",   label: "Rejected",   color: "#EF4444" },
];

const DEPARTMENTS = ["operations", "sales", "academy", "wealth", "digital", "hr", "marketing"];

export default function HR() {
  const [tab, setTab] = useState("employees");
  const [emps, setEmps] = useState([]);
  const [cands, setCands] = useState([]);
  const [stats, setStats] = useState(null);
  const [empOpen, setEmpOpen] = useState(false);
  const [candOpen, setCandOpen] = useState(false);

  const load = async () => {
    const [e, c, s] = await Promise.all([
      api.get("/hr/employees"),
      api.get("/hr/candidates"),
      api.get("/hr/stats"),
    ]);
    setEmps(e.data); setCands(c.data); setStats(s.data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (kind, id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/hr/${kind}/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <AppLayout title="HR">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <StatCard icon={Users}     label="Active employees"    value={stats?.employees_active} accent="#041B46"/>
        <StatCard icon={UserPlus}  label="Open candidates"     value={stats?.candidates_open} accent="#0EA5FF"/>
        <StatCard icon={Target}    label="Team target (₹)"     value={stats ? `₹${(stats.team_target || 0).toLocaleString("en-IN")}` : "—"} accent="#5FBF3A"/>
        <StatCard icon={Target}    label="Progress"            value={stats ? `${stats.target_progress}%` : "—"} accent="#F59E0B"/>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between mb-3">
          <TabsList data-testid="hr-tabs" className="bg-white border border-[var(--miro-border)]">
            <TabsTrigger value="employees" data-testid="tab-employees">Employees</TabsTrigger>
            <TabsTrigger value="recruitment" data-testid="tab-recruitment">Recruitment</TabsTrigger>
          </TabsList>
          {tab === "employees" ? (
            <NewEmployeeDialog open={empOpen} setOpen={setEmpOpen} onCreated={load}/>
          ) : (
            <NewCandidateDialog open={candOpen} setOpen={setCandOpen} onCreated={load}/>
          )}
        </div>

        <TabsContent value="employees">
          <div className="bg-white border border-[var(--miro-border)] rounded-xl overflow-x-auto">
            <table data-testid="employees-table" className="w-full text-sm">
              <thead className="bg-[var(--miro-bg)] text-[var(--miro-text-secondary)]">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Department</th>
                  <th className="px-4 py-3 font-semibold text-right">Monthly target</th>
                  <th className="px-4 py-3"/>
                </tr>
              </thead>
              <tbody>
                {emps.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-[var(--miro-text-muted)]">No employees yet.</td></tr>
                )}
                {emps.map(e => (
                  <tr key={e.id} data-testid={`emp-row-${e.id}`} className="border-t border-[var(--miro-border)]">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[var(--miro-navy)]">{e.name}</div>
                      <div className="text-xs text-[var(--miro-text-muted)]">{e.email}</div>
                    </td>
                    <td className="px-4 py-3 text-[var(--miro-text-secondary)] capitalize">{e.role.replace(/_/g," ")}</td>
                    <td className="px-4 py-3 text-[var(--miro-text-secondary)] capitalize">{e.department}</td>
                    <td className="px-4 py-3 text-right font-mono text-[var(--miro-navy)]">₹{(e.monthly_target || 0).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`emp-delete-${e.id}`} onClick={() => remove("employees", e.id)}
                              className="text-[var(--miro-text-muted)] hover:text-red-600">
                        <Trash2 size={15}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="recruitment">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cands.length === 0 && (
              <div className="col-span-full text-center py-16 text-[var(--miro-text-muted)]">No candidates in the pipeline.</div>
            )}
            {cands.map(c => {
              const st = CAND_STAGES.find(s => s.value === c.stage);
              return (
                <div key={c.id} data-testid={`cand-card-${c.id}`} className="miro-card p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="miro-overline">{c.role_applied}</div>
                      <div className="font-display font-bold text-[var(--miro-navy)] mt-1">{c.name}</div>
                      <div className="text-xs text-[var(--miro-text-muted)] mt-0.5">{c.email || c.phone || "—"}</div>
                    </div>
                    <button data-testid={`cand-delete-${c.id}`} onClick={() => remove("candidates", c.id)} className="text-[var(--miro-text-muted)] hover:text-red-600">
                      <Trash2 size={14}/>
                    </button>
                  </div>
                  <Select value={c.stage} onValueChange={async v => { await api.patch(`/hr/candidates/${c.id}`, { stage: v }); load(); }}>
                    <SelectTrigger data-testid={`cand-stage-${c.id}`} className="mt-3 h-8 text-xs" style={{ background: `${st.color}18`, color: st.color, border: "none" }}>
                      <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>{CAND_STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                  {c.expected_ctc > 0 && (
                    <div className="mt-3 text-xs text-[var(--miro-text-secondary)]">Expected CTC: <span className="font-mono font-semibold text-[var(--miro-navy)]">₹{c.expected_ctc.toLocaleString("en-IN")}</span></div>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="miro-card p-5">
      <div className="flex items-start justify-between">
        <div className="miro-overline">{label}</div>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${accent}18`, color: accent }}>
          <Icon size={17}/>
        </div>
      </div>
      <div className="font-display font-black text-2xl mt-3 text-[var(--miro-navy)]">{value ?? "—"}</div>
    </div>
  );
}

function NewEmployeeDialog({ open, setOpen, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", role: "employee", department: "operations", monthly_target: 0, active: true });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/hr/employees", { ...f, monthly_target: Number(f.monthly_target) || 0 });
      toast.success("Employee added");
      setOpen(false);
      setF({ name: "", email: "", role: "employee", department: "operations", monthly_target: 0, active: true });
      onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-emp-btn" style={{ background: "var(--miro-navy)" }}><Plus size={16} className="mr-1"/> New employee</Button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">New employee</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name *</Label><Input data-testid="new-emp-name" value={f.name} onChange={e=>upd("name",e.target.value)} className="mt-1"/></div>
          <div className="col-span-2"><Label>Email *</Label><Input data-testid="new-emp-email" type="email" value={f.email} onChange={e=>upd("email",e.target.value)} className="mt-1"/></div>
          <div>
            <Label>Role</Label>
            <Select value={f.role} onValueChange={v=>upd("role",v)}>
              <SelectTrigger data-testid="new-emp-role" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Department</Label>
            <Select value={f.department} onValueChange={v=>upd("department",v)}>
              <SelectTrigger data-testid="new-emp-dept" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{DEPARTMENTS.map(d => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Monthly target (₹)</Label><Input data-testid="new-emp-target" type="number" value={f.monthly_target} onChange={e=>upd("monthly_target",e.target.value)} className="mt-1"/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button data-testid="new-emp-submit" disabled={!f.name.trim()||!f.email.trim()||saving} onClick={submit} style={{ background: "var(--miro-navy)" }}>
            {saving ? "Saving…" : "Add employee"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewCandidateDialog({ open, setOpen, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", role_applied: "", stage: "applied", expected_ctc: 0 });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/hr/candidates", { ...f, expected_ctc: Number(f.expected_ctc) || 0 });
      toast.success("Candidate added");
      setOpen(false);
      setF({ name: "", email: "", role_applied: "", stage: "applied", expected_ctc: 0 });
      onCreated?.();
    } catch (e) { toast.error("Create failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-cand-btn" style={{ background: "var(--miro-navy)" }}><Plus size={16} className="mr-1"/> New candidate</Button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">New candidate</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input data-testid="new-cand-name" value={f.name} onChange={e=>upd("name",e.target.value)} className="mt-1"/></div>
          <div><Label>Email</Label><Input data-testid="new-cand-email" type="email" value={f.email} onChange={e=>upd("email",e.target.value)} className="mt-1"/></div>
          <div><Label>Role applied *</Label><Input data-testid="new-cand-role" value={f.role_applied} onChange={e=>upd("role_applied",e.target.value)} className="mt-1"/></div>
          <div>
            <Label>Stage</Label>
            <Select value={f.stage} onValueChange={v=>upd("stage",v)}>
              <SelectTrigger data-testid="new-cand-stage" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{CAND_STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Expected CTC (₹)</Label><Input data-testid="new-cand-ctc" type="number" value={f.expected_ctc} onChange={e=>upd("expected_ctc",e.target.value)} className="mt-1"/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button data-testid="new-cand-submit" disabled={!f.name.trim()||!f.role_applied.trim()||saving} onClick={submit} style={{ background: "var(--miro-navy)" }}>
            {saving ? "Saving…" : "Add candidate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
